/**
 * Comments Reducer (Schema Restructure — DB-001b)
 *
 * State shape:
 *   state.comments = {
 *     byVideoId: {
 *       [videoId]: {
 *         comments: [],
 *         pagination: { currentPage, totalPages, totalComments, hasMore },
 *         loading: false
 *       }
 *     },
 *     activeVideoId: string|null,
 *     modalOpen: false,
 *     preSelectedTab: 'normal',
 *     error: null
 *   }
 *
 * All comment data is scoped by videoId to prevent cross-video bleed (REQ-026).
 * Comments use commentId (UUID) as canonical identifier — never Mongoose _id.
 */
import { CommentTypes } from './types';

const EMPTY_VIDEO_STATE = {
  comments: [],
  pagination: { currentPage: 1, totalPages: 1, totalComments: 0, hasMore: false },
  loading: false
};

const initialState = {
  byVideoId: {},
  activeVideoId: null,
  modalOpen: false,
  preSelectedTab: 'normal',
  error: null
};

/** Helper: return video sub-state or empty default */
const getVideoState = (state, videoId) =>
  state.byVideoId[videoId] || { ...EMPTY_VIDEO_STATE, comments: [], pagination: { ...EMPTY_VIDEO_STATE.pagination } };

/** Helper: immutably set video sub-state */
const setVideoState = (state, videoId, patch) => ({
  ...state,
  byVideoId: {
    ...state.byVideoId,
    [videoId]: { ...getVideoState(state, videoId), ...patch }
  }
});

const commentsReducer = (state = initialState, action) => {
  switch (action.type) {

    // ── Fetch ─────────────────────────────────────────────
    case CommentTypes.FETCH_COMMENTS_REQUEST: {
      if (action.payload?.silent) return state; // skip loading:true for background sync (Phase 4.1)
      const videoId = action.payload?.videoId || state.activeVideoId;
      if (!videoId) return { ...state, error: null };
      return setVideoState(state, videoId, { loading: true });
    }

    case CommentTypes.FETCH_COMMENTS_SUCCESS: {
      const { videoId, comments, currentPage, totalPages, totalComments, hasMore } = action.payload;
      return {
        ...setVideoState(state, videoId, {
          comments: comments || [],
          pagination: {
            currentPage: currentPage || 1,
            totalPages: totalPages || 1,
            totalComments: totalComments || 0,
            hasMore: hasMore || false
          },
          loading: false
        }),
        error: null
      };
    }

    case CommentTypes.FETCH_COMMENTS_FAILURE: {
      const videoId = action.payload?.videoId || state.activeVideoId;
      const error = action.payload?.error || action.payload;
      if (!videoId) return { ...state, error };
      return {
        ...setVideoState(state, videoId, { loading: false }),
        error
      };
    }

    // ── Add comment ───────────────────────────────────────
    case CommentTypes.ADD_COMMENT_REQUEST: {
      // Don't set loading:true — optimistic insert + silent re-fetch handles it (BUG R-6 fix)
      return state;
    }

    case CommentTypes.ADD_COMMENT_SUCCESS: {
      const { videoId, comment } = action.payload;
      if (!videoId) return { ...state, error: null };
      const currentState = getVideoState(state, videoId);
      // Optimistic prepend: show new comment immediately at top (Phase 4.1)
      const updatedComments = comment
        ? [comment, ...(currentState.comments || [])]
        : currentState.comments || [];
      return {
        ...setVideoState(state, videoId, {
          comments: updatedComments,
          loading: false,
          pagination: {
            ...currentState.pagination,
            totalComments: comment
              ? (currentState.pagination?.totalComments || 0) + 1
              : currentState.pagination?.totalComments || 0
          }
        }),
        error: null,
      };
    }

    case CommentTypes.ADD_COMMENT_FAILURE: {
      const videoId = action.payload?.videoId || state.activeVideoId;
      const error = action.payload?.error || action.payload;
      if (!videoId) return { ...state, error };
      return {
        ...setVideoState(state, videoId, { loading: false }),
        error
      };
    }

    // ── Delete comment ────────────────────────────────────
    case CommentTypes.DELETE_COMMENT_REQUEST: {
      const { videoId, commentId } = action.payload;
      if (!videoId) return state;
      const vs = getVideoState(state, videoId);
      // Optimistic: remove the comment from the list
      return setVideoState(state, videoId, {
        comments: vs.comments.filter(c => c.commentId !== commentId)
      });
    }

    case CommentTypes.DELETE_COMMENT_SUCCESS: {
      // Already removed optimistically. Just clear loading/error.
      return { ...state, error: null };
    }

    case CommentTypes.DELETE_COMMENT_FAILURE: {
      const { videoId, deletedComment, error } = action.payload;
      if (!videoId || !deletedComment) return { ...state, error };
      // Rollback: re-insert the deleted comment
      const vs = getVideoState(state, videoId);
      return {
        ...setVideoState(state, videoId, {
          comments: [...vs.comments, deletedComment]
        }),
        error
      };
    }

    // ── Edit comment (Phase 3 Step 3.3) ─────────────────
    case CommentTypes.EDIT_COMMENT_SUCCESS: {
      const { videoId, comment } = action.payload;
      if (!videoId || !comment) return { ...state, error: null };
      const vs = getVideoState(state, videoId);
      return {
        ...setVideoState(state, videoId, {
          comments: vs.comments.map(c =>
            c.commentId === comment.commentId ? { ...c, ...comment } : c
          )
        }),
        error: null
      };
    }

    case CommentTypes.EDIT_COMMENT_FAILURE: {
      const error = action.payload?.error || 'Failed to edit comment';
      return { ...state, error };
    }

    // ── Pagination (scoped) ───────────────────────────────
    case CommentTypes.CHANGE_COMMENTS_PAGE: {
      const { videoId, page } = action.payload;
      if (!videoId) return state;
      const vs = getVideoState(state, videoId);
      return setVideoState(state, videoId, {
        pagination: { ...vs.pagination, currentPage: page }
      });
    }

    // ── Active video ──────────────────────────────────────
    case CommentTypes.SET_ACTIVE_VIDEO_ID:
      return { ...state, activeVideoId: action.payload };

    // ── Reset ─────────────────────────────────────────────
    case CommentTypes.RESET_COMMENTS: {
      const videoId = action.payload?.videoId;
      if (videoId) {
        // Scoped reset — remove one video's comment data
        const { [videoId]: _removed, ...remaining } = state.byVideoId;
        return { ...state, byVideoId: remaining, error: null };
      }
      // Global reset
      return { ...initialState };
    }

    // ── Modal control ─────────────────────────────────────
    case CommentTypes.SHOW_COMMENTS_MODAL: {
      const { videoId, preSelectedTab } = action.payload || {};
      return {
        ...state,
        modalOpen: true,
        activeVideoId: videoId || state.activeVideoId,
        preSelectedTab: preSelectedTab || 'normal'
      };
    }

    case CommentTypes.HIDE_COMMENTS_MODAL:
      return { ...state, modalOpen: false, preSelectedTab: 'normal' };

    default:
      return state;
  }
};

export default commentsReducer;
